import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SectionLabel } from '@/components/ui/section-label';
import type { SynthesisAssignment } from '@/types/api';

// Assignments that no longer resolve to exactly one theme of their own type:
// the target was renamed, deleted or retyped after the run, or two themes now
// share its title, or the finding's own node has gone from the graph (its row
// then reads "(removed)"). Accepting one would fail server-side (404, or 409
// when a title is ambiguous), so accept is not offered here. They are still
// shown, because the alternative is losing them silently. The copy below has to
// hold for all three cases, not just the deleted-theme one.
export function OrphanAssignments({
  orphans,
  labelOf,
  onReject,
  canEdit,
  busy,
}: {
  orphans: SynthesisAssignment[];
  labelOf: (id: string) => string;
  onReject: (a: SynthesisAssignment) => void;
  canEdit: boolean;
  busy: boolean;
}) {
  if (orphans.length === 0) return null;
  return (
    <div className="rounded-lg border border-dashed p-3">
      <SectionLabel className="pb-1">Nowhere to put these</SectionLabel>
      <p className="mb-2 text-xs text-muted-foreground">
        These findings no longer point to a single theme on your graph, so there is nothing to
        attach them to. Dismiss them, or re-synthesise to group them again.
      </p>
      <ul className="space-y-1">
        {orphans.map((a) => (
          <li key={a.id} className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5">
            <span className="min-w-0 flex-1 truncate text-xs">{labelOf(a.itemId)}</span>
            <span className="max-w-[45%] shrink-0 truncate text-[10.5px] text-muted-foreground">
              suggested for {a.existingThemeTitle}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => onReject(a)}
              disabled={!canEdit || busy}
              aria-label={`Dismiss suggestion: ${labelOf(a.itemId)}`}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
